/**
 * @fileoverview Menu item component for the ViewMenu dropdown
 * @active Client component
 */

import { Flex, Icon, Text, type FlexProps } from "@chakra-ui/react";
import type { ReactNode } from "react";

import TrashIcon from "@/assets/svg/trash-list-icon.svg";
// Import the centralized type definition
import type { ViewMenuItemProps } from "@/types/view";

import ListText from "../shared/ListText";

/**
 * Renders an individual item within the ViewMenu dropdown.
 * Displays the item title, icon, and a delete button (if applicable).
 * Handles click events for selection and deletion.
 *
 * @component ViewMenuItem
 * @param {ViewMenuItemProps} props - Component props.
 * @returns {JSX.Element} The rendered menu item.
 */
const ViewMenuItem = ({
  item,
  handler,
  deleteHandler,
  index,
  icon,
  onClose,
  isCurrent,
  style, // Keep style here, it's now optional in the interface
  isView,
  isDelete = true,
}: ViewMenuItemProps): JSX.Element => {
  // Add type annotation
  const onItemHandler = () => {
    handler();
    onClose();
  };

  const onDeleteHandler = (event: React.MouseEvent) => {
    event.stopPropagation();
    deleteHandler(item);
  };

  return (
    <Flex
      py={"0"}
      display={"flex"}
      alignItems={"center"}
      justifyContent={"space-between"}
      columnGap={"12px"}
      bg={isCurrent ? (isView ? "lightBlue2" : "lightGreen2") : "transparent"}
      _hover={{ bg: isView ? "lightBlue2" : "lightGreen2" }}
      h={"56px"}
      px={"22px"}
      cursor={"pointer"}
      onClick={onItemHandler}
      // Remove the problematic style spread
      // {...style} 
    >
      <Flex align={"center"} columnGap={"12px"}>
        <Icon as={icon} w={"40px"} h={"40px"} />
        {/* Provide default empty string for title */}
        <ListText title={item?.title || ""} />
      </Flex>
      {index !== 0 && isDelete ? (
        <Flex direction={"column"} justify={"space-between"} rowGap={"4px"}>
          <Icon as={TrashIcon} cursor={"pointer"} onClick={onDeleteHandler} />
          {!isView && item?.entities?.length ? (
            <Text
              color={"grey"}
              fontSize={"10px"}
              lineHeight={"1.35"}
              letterSpacing={"4%"}
              mt={"2px"}
            >
              {item?.entities?.length || 0}
            </Text>
          ) : null}
        </Flex>
      ) : null}
    </Flex>
  );
};

export default ViewMenuItem;
